import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────────────────────────────────────
// PANEL MAESTRO DE SUSCRIPTORES — solo super-admin (Milena)
//
// - Portero propio: al montar llama GET /api/superadmin/verificar.
//   Si responde 403, muestra "acceso restringido" — el componente es seguro
//   aunque alguien adivine la ruta, porque el backend valida la marca
//   superAdmin directamente en Firestore en cada petición.
// - Lista todos los suscriptores (admins) con plan, estado, vencimiento,
//   sub-usuarios y módulos.
// - Editar plan: plan + estado + fechas + notas → colección `suscripciones`.
// - Editar módulos: el switch de QR/IA y demás, sin tocar Firebase a mano.
//   Convención del sistema: lista vacía = TODOS los módulos.
// ─────────────────────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Catálogo de módulos conocidos (claves reales del sistema, en minúscula).
// Si un suscriptor tiene claves fuera de este catálogo, se muestran y se
// CONSERVAN — el editor nunca elimina claves desconocidas en silencio.
// ✅ FIX PANEL-MODULOS-001 (2026-07-26)
// Faltaban en el catálogo claves que SÍ existen en la navegación (App.js →
// NAV_GRUPOS/TODOS_LOS_MODULOS): vencimientos, comercial, proveedores,
// mi_empresa y usuarios. Al no estar aquí, el editor de módulos no permitía
// activarlas ni desactivarlas: aparecían como texto suelto en las etiquetas del
// suscriptor pero no tenían casilla. 'vencimientos' es justo el módulo del que
// dependen Lucy y Anny, así que era el más molesto de los cinco.
const CATALOGO_MODULOS = [
  { key: 'ordenes',      label: 'Órdenes de servicio' },
  { key: 'cotizaciones', label: 'Cotizaciones' },
  { key: 'clientes',     label: 'Clientes' },
  { key: 'comercial',    label: 'Comercial / Telemercadeo' },
  { key: 'vencimientos', label: 'Vencimientos' },
  { key: 'productos',    label: 'Productos' },
  { key: 'proveedores',  label: 'Proveedores' },
  { key: 'inventarios',  label: 'Inventarios' },
  { key: 'logistica',    label: 'Logística' },
  { key: 'taller',       label: 'Taller' },
  { key: 'caja',         label: 'Caja y Transferencias' },
  { key: 'egresos',      label: 'Egresos' },
  { key: 'compras',      label: 'Compras' },
  { key: 'cxc',          label: 'Cuentas por Cobrar' },
  { key: 'cxp',          label: 'Cuentas por Pagar' },
  { key: 'eri',          label: 'ERI' },
  { key: 'reportes',     label: 'Reportes' },
  { key: 'mi_empresa',   label: 'Mi Empresa' },
  { key: 'usuarios',     label: 'Usuarios' },
  { key: 'qr',           label: 'QR Activos ⭐ (solo invitados)' },
  { key: 'llamadas_ia',  label: 'Llamadas IA — Lucy ⭐ (solo invitados)' },
 { key: 'anny_ia',      label: 'WhatsApp IA Anny ⭐ (solo invitados)' },
  { key: 'inventario_pro', label: 'Inventario Inteligente ⭐ (solo invitados)' },
];

// ✅ INV-KARDEX-003: claves PREMIUM. Semántica opuesta al resto del catálogo:
// la convención "lista vacía = todos los módulos" NO aplica a estas. Solo se
// conceden si están marcadas explícitamente, y el backend las valida igual
// (services/capacidadesTenant.js → PREMIUM). Si aquí se agrega una clave, debe
// agregarse también allá, o el panel prometerá algo que el backend niega.
const CLAVES_PREMIUM = ['llamadas_ia', 'anny_ia', 'inventario_pro'];

const ESTADO_UI = {
  trial:      { txt: 'Prueba',     bg: '#eef2ff', col: '#5b5bd6' },
  activo:     { txt: 'Activo',     bg: '#e9f9ef', col: '#16a34a' },
  suspendido: { txt: 'Suspendido', bg: '#fde8e8', col: '#dc2626' },
  // ✅ SUSCRIPCION-BLOQUEO-001: estados calculados por el sistema
  gracia:     { txt: 'En gracia',  bg: '#fff8e6', col: '#b45309' },
  sin_suscripcion: { txt: 'Sin plan', bg: '#f1f1f8', col: '#6b6b85' },
};

const hoyMas = (dias) => {
  const d = new Date(Date.now() + dias * 86400000);
  return d.toISOString().slice(0, 10);
};

const PanelSuscriptores = () => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [acceso, setAcceso] = useState('verificando'); // verificando | ok | denegado
  const [suscriptores, setSuscriptores] = useState([]);
  const [planes, setPlanes] = useState({});
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  // Modal de plan
  const [editPlan, setEditPlan] = useState(null); // suscriptor en edición
  // ✅ SUSCRIPCION-BLOQUEO-001
  const [editPago, setEditPago] = useState(null);   // suscriptor al que se le registra un pago
  const [formPago, setFormPago] = useState({ monto: '', medio: 'Transferencia', fecha: '', meses: 1, notas: '' });
  const [pagosHist, setPagosHist] = useState([]);
  const [formPlan, setFormPlan] = useState({ plan: '', estado: 'trial', fechaInicio: '', fechaVencimiento: '', notas: '' });

  // Modal de módulos
  const [editMods, setEditMods] = useState(null);
  const [modsSel, setModsSel] = useState([]);

  // ✅ LUCY-MINUTOS-001: tope de minutos de Lucy por suscriptor
  const [editMinutos, setEditMinutos] = useState(null);
  const [minutosCfg, setMinutosCfg] = useState(null);
  const [minutosVal, setMinutosVal] = useState('');

  // ✅ ANNY-CONSUMO-026: consumo de Anny por suscriptor (para facturar)
  const [editConsumo, setEditConsumo] = useState(null);
  const [consumoData, setConsumoData] = useState(null);
  const [topeImgVal, setTopeImgVal] = useState('');

  // ✅ ANNY-NICHO-033: perfil de negocio de Anny por suscriptor (nicho)
  const [editPerfil, setEditPerfil] = useState(null);   // suscriptor en edición
  const [nichosDisp, setNichosDisp] = useState([]);     // plantillas del backend
  const [formPerfil, setFormPerfil] = useState(null);   // campos del perfil

  // ✅ CAPACIDAD-TENANT-002: diagnóstico de órdenes atascadas por falta de módulo
  const [editAtascos, setEditAtascos] = useState(null); // suscriptor en revisión
  const [atascos, setAtascos] = useState(null);         // respuesta del backend
  const [revisando, setRevisando] = useState(false);

  const [guardando, setGuardando] = useState(false);

  // ─── Cargar lista ──────────────────────────────────────────────────────────
  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const res = await axios.get(`${API}/superadmin/suscriptores`, { headers });
      setSuscriptores(res.data.suscriptores || []);
      setPlanes(res.data.planes || {});
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar suscriptores');
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Portero: verificar super-admin al montar ─────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/superadmin/verificar`, { headers });
        setAcceso('ok');
        cargar();
      } catch {
        setAcceso('denegado');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flashExito = (msg) => {
    setExito(msg);
    setTimeout(() => setExito(''), 3500);
  };

  // ─── Abrir modal de plan ───────────────────────────────────────────────────
  const abrirPlan = (s) => {
    setFormPlan({
      plan: s.plan || 'empresa',
      estado: s.estado || 'trial',
      fechaInicio: s.fechaInicio || new Date().toISOString().slice(0, 10),
      fechaVencimiento: s.fechaVencimiento || hoyMas(14),
      notas: s.notas || ''
    });
    setEditPlan(s);
  };

  // ✅ SUSCRIPCION-BLOQUEO-001: registrar pago / gracia / suspender
  const abrirPago = async (s) => {
    setEditPago(s);
    setPagosHist([]);
    const precio = (planes[s.plan]?.precio) || '';
    setFormPago({ monto: precio ? String(precio) : '', medio: 'Transferencia', fecha: new Date().toISOString().slice(0, 10), meses: 1, notas: '' });
    try {
      const r = await axios.get(`${API}/superadmin/suscriptores/${s.adminId}/pagos`, { headers });
      setPagosHist(r.data.pagos || []);
    } catch (e) { /* sin historial */ }
  };

  const registrarPago = async () => {
    setGuardando(true); setError('');
    try {
      const r = await axios.post(`${API}/superadmin/suscriptores/${editPago.adminId}/pago`, formPago, { headers });
      setEditPago(null);
      flashExito(`Pago registrado — la cuenta queda activa hasta ${r.data.fechaVencimiento}`);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo registrar el pago');
    } finally { setGuardando(false); }
  };

  const extenderGracia = async (s) => {
    const dias = window.prompt(`¿Cuántos días de gracia le das a ${s.empresa || s.nombre}? (1 a 30)`, '3');
    if (!dias) return;
    setGuardando(true); setError('');
    try {
      const r = await axios.post(`${API}/superadmin/suscriptores/${s.adminId}/gracia`, { dias: Number(dias) }, { headers });
      flashExito(`Gracia extendida hasta ${r.data.graciaHasta}`);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo extender la gracia');
    } finally { setGuardando(false); }
  };

  const suspenderAhora = async (s) => {
    const motivo = window.prompt(`Motivo para suspender a ${s.empresa || s.nombre} (se muestra al suscriptor):`, 'Suscripción vencida sin pago');
    if (motivo === null) return;
    if (!window.confirm('La empresa quedará bloqueada de inmediato para todos sus usuarios. ¿Continuar?')) return;
    setGuardando(true); setError('');
    try {
      await axios.post(`${API}/superadmin/suscriptores/${s.adminId}/suspender`, { motivo }, { headers });
      flashExito('Cuenta suspendida');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo suspender');
    } finally { setGuardando(false); }
  };

  // ✅ SUSCRIPTOR-ELIMINAR-001: cerrar cuenta (borrado lógico + sub-usuarios)
  const eliminarSuscriptor = async (s) => {
    const escrito = window.prompt(`Vas a CERRAR la cuenta de ${s.empresa || s.nombre}.\nSus usuarios no podrán entrar más y desaparece de este panel (sus datos no se borran).\n\nEscribe el email ${s.email} para confirmar:`);
    if (escrito === null) return;
    setGuardando(true); setError('');
    try {
      const r = await axios.delete(`${API}/superadmin/suscriptores/${s.adminId}`, { headers, data: { confirmacion: escrito } });
      flashExito(`Cuenta cerrada (${r.data.subUsuariosDesactivados} sub-usuario(s) desactivados)`);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo eliminar');
    } finally { setGuardando(false); }
  };

  const guardarPlan = async () => {
    if (!formPlan.fechaVencimiento) { setError('La fecha de vencimiento es obligatoria'); return; }
    setGuardando(true);
    setError('');
    try {
      await axios.put(`${API}/superadmin/suscriptores/${editPlan.adminId}/plan`, formPlan, { headers });
      setEditPlan(null);
      flashExito('Suscripción guardada');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar la suscripción');
    } finally {
      setGuardando(false);
    }
  };

  // ─── ✅ LUCY-MINUTOS-001: tope de minutos de Lucy por suscriptor ───────────
  // Antes el tope vivía solo en la variable de entorno (120 min iguales para
  // todos) y solo se cambiaba tocando la base de datos a mano. Ahora se
  // asigna por suscriptor desde aquí, según el plan que se le venda.
  const abrirMinutos = async (s) => {
    setEditMinutos(s);
    setMinutosCfg(null);
    setMinutosVal('');
    try {
      const r = await axios.get(`${API}/llamadas-ia/superadmin/config/${s.adminId}`, { headers });
      setMinutosCfg(r.data);
      setMinutosVal(String(r.data.topeMinutosMes ?? ''));
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo leer la configuración de Lucy');
    }
  };

  // ✅ ANNY-CONSUMO-026: consumo de Anny del suscriptor (mes actual + 5 previos)
  const abrirConsumoAnny = async (s) => {
    setEditConsumo(s);
    setConsumoData(null);
    try {
      const r = await axios.get(`${API}/anny/superadmin/consumo/${s.adminId}`, { headers });
      setConsumoData(r.data);
      setTopeImgVal(String(r.data.limites?.imagenesMes ?? ''));
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo leer el consumo de Anny');
    }
  };

  const guardarLimitesAnny = async () => {
    const tope = Number(topeImgVal);
    if (!Number.isFinite(tope) || tope < 0) { setError('El tope debe ser 0 o mayor (0 = sin límite)'); return; }
    setGuardando(true);
    setError('');
    try {
      await axios.put(`${API}/anny/limites/${editConsumo.adminId}`,
        { imagenesMes: tope }, { headers });
      setEditConsumo(null);
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo guardar el tope');
    } finally {
      setGuardando(false);
    }
  };

  // ✅ ANNY-NICHO-033: abrir/guardar el perfil de negocio de Anny.
  // El nicho precarga la plantilla en el backend; los demás campos afinan.
  const abrirPerfilAnny = async (s) => {
    setEditPerfil(s);
    setFormPerfil(null);
    try {
      const r = await axios.get(`${API}/anny/perfil/${s.adminId}`, { headers });
      const p = r.data.perfil || {};
      setNichosDisp(r.data.nichos || []);
      setFormPerfil({
        nicho: p.nicho || '',
        nombreAgente: p.nombreAgente || 'Anny',
        empresa: p.empresa || '',
        queVende: p.queVende || '',
        reglasNegocio: p.reglasNegocio || '',
        mediosPago: p.mediosPago || '',
        avisarVentaCliente: p.avisarVentaCliente === true,
        modoSoloAvisos: p.modoSoloAvisos === true,   // ✅ ANNY-AVISOS-065
        notificarEscalamientoA: p.notificarEscalamientoA || ''
      });
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo leer el perfil de Anny');
    }
  };

  const guardarPerfilAnny = async () => {
    setGuardando(true);
    setError('');
    try {
      await axios.put(`${API}/anny/perfil/${editPerfil.adminId}`, formPerfil, { headers });
      setEditPerfil(null);
      flashExito('Perfil de Anny guardado');
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo guardar el perfil de Anny');
    } finally {
      setGuardando(false);
    }
  };

  const guardarMinutos = async () => {
    const tope = Number(minutosVal);
    if (!Number.isFinite(tope) || tope < 0) { setError('El tope debe ser un número mayor o igual a 0'); return; }
    setGuardando(true);
    setError('');
    try {
      await axios.put(`${API}/llamadas-ia/superadmin/config/${editMinutos.adminId}`,
        { topeMinutosMes: tope }, { headers });
      setEditMinutos(null);
      flashExito(`Tope de Lucy actualizado a ${tope} min/mes`);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar el tope de minutos');
    } finally {
      setGuardando(false);
    }
  };

  // ─── Abrir modal de módulos ────────────────────────────────────────────────
  const abrirMods = (s) => {
    setModsSel(s.modulos || []);
    setEditMods(s);
  };

  const toggleMod = (key) => {
    setModsSel(prev => prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]);
  };

  const guardarMods = async () => {
    setGuardando(true);
    setError('');
    try {
      await axios.put(`${API}/superadmin/suscriptores/${editMods.adminId}/modulos`, { modulos: modsSel }, { headers });
      setEditMods(null);
      flashExito('Módulos actualizados');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al actualizar módulos');
    } finally {
      setGuardando(false);
    }
  };

  // ─── ✅ CAPACIDAD-TENANT-002: órdenes atascadas ────────────────────────────
  // Un suscriptor sin módulo Taller que crea su categoría "Recarga y
  // Mantenimiento" generaba órdenes que nacían en 'en_taller' — estado cuya
  // única salida la ejecuta el módulo Taller, que no tiene. Quedaban muertas.
  // El fix CAPACIDAD-TENANT-001 impide que vuelvan a nacer así; esto desatasca
  // las que ya existían, desde aquí, sin entrar con el usuario del suscriptor.
  const revisarAtascos = async (s2, aplicar = false) => {
    setRevisando(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/orders/reparar-sin-taller`,
        { adminId: s2.adminId, aplicar },
        { headers }
      );
      setAtascos(data);
      setEditAtascos(s2);
      if (aplicar && data.corregidas > 0) flashExito(`${data.corregidas} orden(es) desatascada(s)`);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al revisar órdenes atascadas');
    } finally {
      setRevisando(false);
    }
  };

  // ─── Estilos ───────────────────────────────────────────────────────────────
  const st = {
    page: { padding: '24px 16px', maxWidth: 1100, margin: '0 auto', fontFamily: 'inherit' },
    h1: { fontSize: 22, fontWeight: 800, color: '#14142b', margin: 0 },
    sub: { color: '#6b6b85', fontSize: 13, marginTop: 4 },
    alertErr: { background: '#fde8e8', color: '#dc2626', padding: '10px 14px', borderRadius: 10, fontSize: 13, margin: '14px 0' },
    alertOk: { background: '#e9f9ef', color: '#16a34a', padding: '10px 14px', borderRadius: 10, fontSize: 13, margin: '14px 0' },
    card: { background: '#fff', border: '1.5px solid #e8e8f4', borderRadius: 14, padding: 18, marginTop: 14 },
    rowTop: { display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', alignItems: 'flex-start' },
    nombre: { fontWeight: 700, fontSize: 15, color: '#14142b' },
    email: { color: '#6b6b85', fontSize: 12.5 },
    badge: (e) => ({ display: 'inline-block', padding: '3px 11px', borderRadius: 99, fontSize: 11.5, fontWeight: 700, background: e.bg, color: e.col }),
    datos: { display: 'flex', flexWrap: 'wrap', gap: '8px 22px', marginTop: 12, fontSize: 13, color: '#3d3d5c' },
    dato: { display: 'flex', flexDirection: 'column' },
    datoL: { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.08em', color: '#9a9ab5', fontWeight: 600 },
    datoV: { fontWeight: 600, marginTop: 2 },
    chips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 },
    chip: { background: '#f6f6fc', border: '1px solid #e8e8f4', borderRadius: 99, padding: '3px 10px', fontSize: 11.5, color: '#3d3d5c' },
    chipQR: { background: '#f3eefe', border: '1px solid #ddd0fb', borderRadius: 99, padding: '3px 10px', fontSize: 11.5, color: '#8b5cf6', fontWeight: 700 },
    btns: { display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' },
    btn: { background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
    btnGhost: { background: '#fff', color: '#14142b', border: '1.5px solid #e8e8f4', borderRadius: 10, padding: '8px 15px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(20,20,43,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 },
    modal: { background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 460, maxHeight: '88vh', overflowY: 'auto' },
    label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#3d3d5c', margin: '14px 0 5px' },
    input: { width: '100%', padding: '10px 12px', border: '1.5px solid #e8e8f4', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' },
    aviso: { background: '#fff8e6', border: '1px solid #f3d98a', color: '#8a6d1a', borderRadius: 10, padding: '9px 12px', fontSize: 12, marginTop: 12 },
    modRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid #f1f1f8', fontSize: 13.5, cursor: 'pointer' },
  };

  // ─── Estados de acceso ─────────────────────────────────────────────────────
  if (acceso === 'verificando') {
    return <div style={{ ...st.page, color: '#6b6b85' }}>Verificando acceso…</div>;
  }
  if (acceso === 'denegado') {
    return (
      <div style={st.page}>
        <div style={st.alertErr}>Acceso restringido. Este panel es exclusivo del administrador de la plataforma.</div>
      </div>
    );
  }

  // ─── Render principal ──────────────────────────────────────────────────────
  return (
    <div style={st.page}>
      <h1 style={st.h1}>Panel de Suscriptores</h1>
      <p style={st.sub}>Administra planes, vencimientos y módulos de cada suscriptor de Control360.</p>

      {error && <div style={st.alertErr}>{error}</div>}
      {exito && <div style={st.alertOk}>✓ {exito}</div>}
      {cargando && <div style={{ color: '#6b6b85', marginTop: 14 }}>Cargando…</div>}

      {!cargando && suscriptores.map((s) => {
        // ✅ SUSCRIPCION-BLOQUEO-001: el badge muestra lo que el sistema APLICA (bloqueo real)
        const e = s.bloqueada
          ? { txt: '🔒 Bloqueada', bg: '#fde8e8', col: '#b91c1c' }
          : (ESTADO_UI[s.estadoCalculado] || ESTADO_UI[s.estado] || { txt: 'Sin plan', bg: '#f1f1f8', col: '#6b6b85' });
        const venceTxt = s.fechaVencimiento
          ? `${s.fechaVencimiento}${s.diasRestantes !== null ? ` · ${s.diasRestantes >= 0 ? `${s.diasRestantes} días` : `vencido hace ${Math.abs(s.diasRestantes)} días`}` : ''}`
          : '—';
        const venceColor = s.diasRestantes === null ? '#3d3d5c' : s.diasRestantes < 0 ? '#dc2626' : s.diasRestantes <= 5 ? '#d97706' : '#16a34a';
        const graciaTxt = s.graciaHasta ? ` · gracia hasta ${s.graciaHasta}` : (s.diasRestantes !== null && s.diasRestantes < 0 && !s.bloqueada ? ` · se bloquea en ${Math.max(0, (s.diasGracia ?? 5) + s.diasRestantes + 1)} día(s)` : '');

        return (
          <div key={s.adminId} style={st.card}>
            <div style={st.rowTop}>
              <div>
                <div style={st.nombre}>
                  {s.empresa || s.nombre || s.email}
                  {s.superAdmin && <span style={{ marginLeft: 8, fontSize: 11, color: '#8b5cf6', fontWeight: 700 }}>★ Plataforma</span>}
                </div>
                <div style={st.email}>{s.nombre} · {s.email}</div>
              </div>
              <span style={st.badge(e)}>{e.txt}</span>
            </div>

            <div style={st.datos}>
              <div style={st.dato}>
                <span style={st.datoL}>Plan</span>
                <span style={st.datoV}>{s.planNombre || 'Sin asignar'}</span>
              </div>
              <div style={st.dato}>
                <span style={st.datoL}>Vence</span>
                <span style={{ ...st.datoV, color: venceColor }}>{venceTxt}{graciaTxt}</span>
              </div>
              <div style={st.dato}>
                <span style={st.datoL}>Sub-usuarios</span>
                <span style={st.datoV}>{s.subUsuarios}</span>
              </div>
              <div style={st.dato}>
                <span style={st.datoL}>Login</span>
                <span style={{ ...st.datoV, color: s.activo ? '#16a34a' : '#dc2626' }}>{s.activo ? 'Habilitado' : 'Desactivado'}</span>
              </div>
            </div>

            <div style={st.chips}>
              {(s.modulos || []).length === 0
                ? <span style={st.chip}>Todos los módulos</span>
                : s.modulos.map(m => (
                    <span key={m} style={(m === 'qr' || CLAVES_PREMIUM.includes(m)) ? st.chipQR : st.chip}>
                      {CATALOGO_MODULOS.find(c => c.key === m)?.label.replace(' ⭐ (solo invitados)', ' ⭐') || m}
                    </span>
                  ))}
            </div>

            <div style={st.btns}>
              <button style={st.btn} onClick={() => abrirPlan(s)}>
                {s.plan ? 'Editar plan' : 'Asignar plan'}
              </button>
              {/* ✅ SUSCRIPCION-BLOQUEO-001 */}
              {s.plan && !s.superAdmin && (
                <>
                  <button style={{ ...st.btn, background: '#16a34a' }} onClick={() => abrirPago(s)}>💳 Registrar pago</button>
                  {(s.bloqueada || (s.diasRestantes !== null && s.diasRestantes < 0)) && (
                    <button style={st.btnGhost} onClick={() => extenderGracia(s)}>⏳ Gracia</button>
                  )}
                  {!s.bloqueada && (
                    <button style={{ ...st.btnGhost, color: '#b91c1c' }} onClick={() => suspenderAhora(s)}>⛔ Suspender</button>
                  )}
                </>
              )}
              <button style={st.btnGhost} onClick={() => abrirMods(s)}>Módulos</button>
              {/* ✅ SUSCRIPTOR-ELIMINAR-001 */}
              {!s.superAdmin && (
                <button style={{ ...st.btnGhost, color: '#6b6b85', marginLeft: 'auto' }} onClick={() => eliminarSuscriptor(s)} title="Cerrar esta cuenta">🗑 Eliminar</button>
              )}
              {/* ✅ CAPACIDAD-TENANT-002: solo tiene sentido si NO tiene Taller.
                  Con Taller activo su flujo es el correcto y no hay nada que reparar.
                  Lista vacía = todos los módulos = sí tiene Taller. */}
              {(s.modulos || []).length > 0 && !(s.modulos || []).includes('taller') && (
                <button style={st.btnGhost} disabled={revisando}
                  onClick={() => revisarAtascos(s, false)}>
                  {revisando ? 'Revisando…' : '🔧 Órdenes atascadas'}
                </button>
              )}
              {/* ✅ LUCY-MINUTOS-001: solo si el suscriptor tiene Lucy activa */}
              {(s.modulos || []).includes('llamadas_ia') && (
                <button style={st.btnGhost} onClick={() => abrirMinutos(s)}>📞 Minutos Lucy</button>
              )}
              {/* ✅ ANNY-CONSUMO-026: consumo real del suscriptor, para facturarle.
                  Lista de módulos vacía = todos activos (invariante del sistema). */}
              {((s.modulos || []).length === 0 || (s.modulos || []).includes('anny_ia')) && (
                <>
                  <button style={st.btnGhost} onClick={() => abrirConsumoAnny(s)}>💬 Consumo Anny</button>
                  {/* ✅ ANNY-NICHO-033: nicho + perfil de negocio del suscriptor */}
                  <button style={st.btnGhost} onClick={() => abrirPerfilAnny(s)}>🧬 Perfil Anny</button>
                </>
              )}
            </div>
          </div>
        );
      })}

      {!cargando && suscriptores.length === 0 && !error && (
        <div style={{ ...st.card, color: '#6b6b85', textAlign: 'center' }}>No hay suscriptores registrados todavía.</div>
      )}

      {/* ─── ✅ ANNY-CONSUMO-026 · MODAL: CONSUMO DE ANNY ─── */}
      {editConsumo && (
        <div style={st.overlay} onClick={() => !guardando && setEditConsumo(null)}>
          <div style={st.modal} onClick={(ev) => ev.stopPropagation()}>
            <h2 style={{ ...st.h1, fontSize: 17 }}>
              💬 Consumo de Anny — {editConsumo.empresa || editConsumo.nombre || editConsumo.email}
            </h2>

            {!consumoData ? (
              <div style={{ color: '#6b6b85', fontSize: 13, marginTop: 14 }}>Cargando consumo…</div>
            ) : (
              <>
                {/* Mes en curso — lo que se le factura */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 14 }}>
                  <div style={{ background: '#f5f3ff', borderRadius: 10, padding: '11px 13px' }}>
                    <div style={{ fontSize: 11, color: '#6b6b85', fontWeight: 700 }}>MENSAJES ESTE MES</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#6d28d9' }}>
                      {consumoData.actual.mensajes.toLocaleString('es-CO')}
                    </div>
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '11px 13px' }}>
                    <div style={{ fontSize: 11, color: '#6b6b85', fontWeight: 700 }}>COSTO REAL</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>
                      US${consumoData.actual.costoUSD.toFixed(2)}
                    </div>
                    <div style={{ fontSize: 10.5, color: '#94a3b8' }}>
                      US${consumoData.costoPorMensajeUSD.toFixed(5)} por mensaje
                    </div>
                  </div>
                </div>

                <div style={{ fontSize: 12, color: '#6b6b85', marginTop: 10 }}>
                  {consumoData.actual.imagenes} fotos · {consumoData.actual.audios} audios ·{' '}
                  {(consumoData.actual.tokensEntrada + consumoData.actual.tokensSalida).toLocaleString('es-CO')} tokens
                </div>

                {/* Historial: sirve para fijar la tarifa viendo la tendencia */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                    Últimos 6 meses
                  </div>
                  {consumoData.historial.map(h => (
                    <div key={h.periodo} style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: 12, padding: '5px 0', borderBottom: '1px solid #f1f5f9',
                    }}>
                      <span style={{ color: '#6b6b85' }}>{h.periodo}</span>
                      <span style={{ color: '#0f172a', fontWeight: 700 }}>
                        {h.mensajes.toLocaleString('es-CO')} msj · US${h.costoUSD.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Freno: tope de fotos del mes */}
                <div style={{ marginTop: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                    Tope de fotos por mes (0 = sin límite)
                  </label>
                  <input
                    type="number" min="0" value={topeImgVal}
                    onChange={e => setTopeImgVal(e.target.value)}
                    style={{ ...st.input, marginTop: 5 }}
                  />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    Al alcanzarlo, las fotos dejan de analizarse y el mensaje escala a un asesor.
                    Va en {consumoData.actual.imagenes} este mes.
                  </div>
                </div>

                <div style={{ ...st.btns, marginTop: 16 }}>
                  <button style={st.btnGhost} onClick={() => setEditConsumo(null)} disabled={guardando}>
                    Cerrar
                  </button>
                  <button style={st.btn} onClick={guardarLimitesAnny} disabled={guardando}>
                    {guardando ? 'Guardando…' : 'Guardar tope'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── ✅ ANNY-NICHO-033 · MODAL: PERFIL DE NEGOCIO DE ANNY ─── */}
      {editPerfil && (
        <div style={st.overlay} onClick={() => !guardando && setEditPerfil(null)}>
          <div style={st.modal} onClick={(ev) => ev.stopPropagation()}>
            <h2 style={{ ...st.h1, fontSize: 17 }}>
              🧬 Perfil de Anny — {editPerfil.empresa || editPerfil.nombre || editPerfil.email}
            </h2>

            {!formPerfil ? (
              <div style={{ color: '#6b6b85', fontSize: 13, marginTop: 14 }}>Cargando perfil…</div>
            ) : (
              <>
                <label style={st.label}>Actividad económica (nicho)</label>
                <select style={st.input} value={formPerfil.nicho}
                  onChange={e => setFormPerfil({ ...formPerfil, nicho: e.target.value })}>
                  <option value="">— Sin plantilla (perfil manual) —</option>
                  {nichosDisp.map(n => (
                    <option key={n.id} value={n.id}>{n.etiqueta}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  Al guardar con un nicho, Anny se precarga con las reglas de esa actividad.
                  Los campos de abajo que llenes tienen prioridad sobre la plantilla.
                </div>

                <label style={st.label}>Nombre del agente</label>
                <input type="text" style={st.input} value={formPerfil.nombreAgente}
                  onChange={e => setFormPerfil({ ...formPerfil, nombreAgente: e.target.value })} />

                <label style={st.label}>Nombre de la empresa (como se presenta al cliente)</label>
                <input type="text" style={st.input} value={formPerfil.empresa}
                  onChange={e => setFormPerfil({ ...formPerfil, empresa: e.target.value })} />

                <label style={st.label}>Qué vende (opcional — la plantilla ya trae uno)</label>
                <input type="text" style={st.input} value={formPerfil.queVende}
                  onChange={e => setFormPerfil({ ...formPerfil, queVende: e.target.value })} />

                <label style={st.label}>Reglas propias del negocio (opcional)</label>
                <textarea style={{ ...st.input, minHeight: 70, resize: 'vertical' }} value={formPerfil.reglasNegocio}
                  onChange={e => setFormPerfil({ ...formPerfil, reglasNegocio: e.target.value })} />

                <label style={st.label}>Medios de pago (ej: Nequi 300..., Bancolombia ahorros ...)</label>
                <input type="text" style={st.input} value={formPerfil.mediosPago}
                  onChange={e => setFormPerfil({ ...formPerfil, mediosPago: e.target.value })} />

                <label style={{ ...st.modRow, marginTop: 12, border: 'none' }}>
                  <input type="checkbox" checked={formPerfil.avisarVentaCliente}
                    onChange={e => setFormPerfil({ ...formPerfil, avisarVentaCliente: e.target.checked })} />
                  <span>
                    <b>Confirmar ventas por WhatsApp</b> — al registrar una orden, Anny le escribe
                    al cliente con el resumen y los medios de pago.
                  </span>
                </label>

                {/* ✅ ANNY-AVISOS-065 — apagar la atención sin apagar el módulo */}
                <label style={{ ...st.modRow, marginTop: 8, border: 'none' }}>
                  <input type="checkbox" checked={formPerfil.modoSoloAvisos}
                    onChange={e => setFormPerfil({ ...formPerfil, modoSoloAvisos: e.target.checked })} />
                  <span>
                    <b>Modo solo avisos</b> — Anny deja de atender conversaciones. Sigue enviando
                    cobranza, confirmación de ventas, avisos de taller y vencimientos, y sigue
                    procesando comprobantes de pago. A quien escriba por su cuenta le responde un
                    acuse fijo con el horario y avisa al equipo.
                  </span>
                </label>

                <label style={st.label}>WhatsApp para avisos de escalamiento</label>
                <input type="text" style={st.input} placeholder="3001234567" value={formPerfil.notificarEscalamientoA}
                  onChange={e => setFormPerfil({ ...formPerfil, notificarEscalamientoA: e.target.value })} />

                <div style={{ ...st.btns, justifyContent: 'flex-end' }}>
                  <button style={st.btnGhost} disabled={guardando} onClick={() => setEditPerfil(null)}>Cancelar</button>
                  <button style={st.btn} disabled={guardando} onClick={guardarPerfilAnny}>
                    {guardando ? 'Guardando…' : 'Guardar perfil'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── ✅ LUCY-MINUTOS-001 · MODAL: MINUTOS DE LUCY ─── */}
      {editMinutos && (
        <div style={st.overlay} onClick={() => !guardando && setEditMinutos(null)}>
          <div style={st.modal} onClick={(ev) => ev.stopPropagation()}>
            <h2 style={{ ...st.h1, fontSize: 17 }}>📞 Minutos de Lucy — {editMinutos.empresa || editMinutos.nombre || editMinutos.email}</h2>

            {!minutosCfg ? (
              <div style={{ color: '#6b6b85', fontSize: 13, marginTop: 14 }}>Cargando configuración...</div>
            ) : (
              <>
                <div style={{ background: '#f7f7fb', borderRadius: 10, padding: '12px 14px', marginTop: 14, fontSize: 13, color: '#3d3d5c' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span>Consumido este mes</span>
                    <strong>{minutosCfg.minutosConsumidosMes || 0} min</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Tope actual</span>
                    <strong>{minutosCfg.topeMinutosMes} min/mes</strong>
                  </div>
                </div>

                <label style={st.label}>Tope de minutos por mes</label>
                <input type="number" min="0" style={st.input} value={minutosVal}
                  onChange={e => setMinutosVal(e.target.value)} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  {[60, 120, 300, 600].map(v => (
                    <button key={v} style={{ ...st.btnGhost, padding: '5px 10px', fontSize: 11.5 }}
                      onClick={() => setMinutosVal(String(v))}>{v} min</button>
                  ))}
                </div>
                <div style={{ color: '#6b6b85', fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
                  Cuando el suscriptor llega a su tope, Lucy deja de llamar por ese mes y queda registrado.
                  El consumo se reinicia solo cada mes — cambiar el tope no borra el histórico.
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                  <button style={st.btn} onClick={guardarMinutos} disabled={guardando}>
                    {guardando ? 'Guardando...' : 'Guardar tope'}
                  </button>
                  <button style={st.btnGhost} onClick={() => setEditMinutos(null)} disabled={guardando}>Cancelar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── MODAL: PLAN ─── */}
      {/* ─── ✅ SUSCRIPCION-BLOQUEO-001 · MODAL: REGISTRAR PAGO ─── */}
      {editPago && (
        <div style={st.overlay} onClick={() => !guardando && setEditPago(null)}>
          <div style={st.modal} onClick={(ev) => ev.stopPropagation()}>
            <h2 style={{ ...st.h1, fontSize: 17 }}>💳 Registrar pago — {editPago.empresa || editPago.nombre || editPago.email}</h2>
            <div style={{ fontSize: 12.5, color: '#6b6b85', marginTop: 4 }}>
              Plan {editPago.planNombre || editPago.plan} · vence {editPago.fechaVencimiento || '—'}{editPago.bloqueada ? ' · 🔒 bloqueada' : ''}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
              <div>
                <label style={st.label}>Monto</label>
                <input type="number" style={st.input} value={formPago.monto} onChange={e => setFormPago({ ...formPago, monto: e.target.value })} />
              </div>
              <div>
                <label style={st.label}>Medio</label>
                <select style={st.input} value={formPago.medio} onChange={e => setFormPago({ ...formPago, medio: e.target.value })}>
                  {['Transferencia', 'Nequi', 'Daviplata', 'Efectivo', 'Otro'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={st.label}>Fecha del pago</label>
                <input type="date" style={st.input} value={formPago.fecha} onChange={e => setFormPago({ ...formPago, fecha: e.target.value })} />
              </div>
              <div>
                <label style={st.label}>Meses que cubre</label>
                <select style={st.input} value={formPago.meses} onChange={e => setFormPago({ ...formPago, meses: Number(e.target.value) })}>
                  {[1, 2, 3, 6, 12].map(m => <option key={m} value={m}>{m} {m === 1 ? 'mes' : 'meses'}</option>)}
                </select>
              </div>
            </div>
            <label style={st.label}>Notas (opcional)</label>
            <input type="text" style={st.input} value={formPago.notas} onChange={e => setFormPago({ ...formPago, notas: e.target.value })} placeholder="Ref. del comprobante, acuerdo, etc." />

            <div style={{ fontSize: 12, color: '#3d3d5c', background: '#f5f3ff', borderRadius: 8, padding: '8px 10px', marginTop: 10 }}>
              El vencimiento se corre {formPago.meses} mes(es) desde {editPago.diasRestantes > 0 ? 'el vencimiento actual (no pierde días)' : 'hoy'}. La cuenta queda activa y se desbloquea de inmediato.
            </div>

            {pagosHist.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#6b6b85', textTransform: 'uppercase', marginBottom: 4 }}>Últimos pagos</div>
                {pagosHist.slice(0, 5).map(p => (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '4px 0', borderBottom: '1px solid #f1f1f8' }}>
                    <span>{p.fecha} · {p.medio}</span>
                    <span style={{ fontWeight: 700 }}>${Number(p.monto || 0).toLocaleString('es-CO')} → {p.vencimientoNuevo}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ ...st.btns, justifyContent: 'flex-end' }}>
              <button style={st.btnGhost} disabled={guardando} onClick={() => setEditPago(null)}>Cancelar</button>
              <button style={{ ...st.btn, background: '#16a34a' }} disabled={guardando || !formPago.monto} onClick={registrarPago}>
                {guardando ? 'Guardando…' : 'Registrar y activar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editPlan && (
        <div style={st.overlay} onClick={() => !guardando && setEditPlan(null)}>
          <div style={st.modal} onClick={(ev) => ev.stopPropagation()}>
            <h2 style={{ ...st.h1, fontSize: 17 }}>Suscripción — {editPlan.empresa || editPlan.nombre || editPlan.email}</h2>

            <label style={st.label}>Plan</label>
            <select style={st.input} value={formPlan.plan} onChange={e => setFormPlan({ ...formPlan, plan: e.target.value })}>
              {Object.entries(planes).map(([k, p]) => (
                <option key={k} value={k}>{p.nombre} — ${Number(p.precio).toLocaleString('es-CO')}/mes</option>
              ))}
            </select>

            <label style={st.label}>Estado</label>
            <select style={st.input} value={formPlan.estado} onChange={e => setFormPlan({ ...formPlan, estado: e.target.value })}>
              <option value="trial">Prueba (trial)</option>
              <option value="activo">Activo (pagando)</option>
              <option value="suspendido">Suspendido</option>
            </select>

            <label style={st.label}>Fecha de inicio</label>
            <input type="date" style={st.input} value={formPlan.fechaInicio} onChange={e => setFormPlan({ ...formPlan, fechaInicio: e.target.value })} />

            <label style={st.label}>Fecha de vencimiento *</label>
            <input type="date" style={st.input} value={formPlan.fechaVencimiento} onChange={e => setFormPlan({ ...formPlan, fechaVencimiento: e.target.value })} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button style={{ ...st.btnGhost, padding: '5px 10px', fontSize: 11.5 }} onClick={() => setFormPlan({ ...formPlan, fechaVencimiento: hoyMas(14) })}>+14 días</button>
              <button style={{ ...st.btnGhost, padding: '5px 10px', fontSize: 11.5 }} onClick={() => setFormPlan({ ...formPlan, fechaVencimiento: hoyMas(30) })}>+1 mes</button>
              <button style={{ ...st.btnGhost, padding: '5px 10px', fontSize: 11.5 }} onClick={() => setFormPlan({ ...formPlan, fechaVencimiento: hoyMas(365) })}>+1 año</button>
            </div>

            <label style={st.label}>Notas</label>
            <input type="text" style={st.input} placeholder="Ej: pagó por Nequi, cliente referido…" value={formPlan.notas} onChange={e => setFormPlan({ ...formPlan, notas: e.target.value })} />

            {formPlan.estado === 'suspendido' && (
              <div style={st.aviso}>En esta versión la suspensión es informativa: aún no bloquea el ingreso del suscriptor. El bloqueo automático llega en la siguiente iteración.</div>
            )}

            <div style={{ ...st.btns, justifyContent: 'flex-end' }}>
              <button style={st.btnGhost} disabled={guardando} onClick={() => setEditPlan(null)}>Cancelar</button>
              <button style={st.btn} disabled={guardando} onClick={guardarPlan}>{guardando ? 'Guardando…' : 'Guardar suscripción'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: MÓDULOS ─── */}
      {editMods && (
        <div style={st.overlay} onClick={() => !guardando && setEditMods(null)}>
          <div style={st.modal} onClick={(ev) => ev.stopPropagation()}>
            <h2 style={{ ...st.h1, fontSize: 17 }}>Módulos — {editMods.empresa || editMods.nombre || editMods.email}</h2>
            <p style={{ ...st.sub, marginTop: 6 }}>Lo que no esté activo no aparece en el menú del suscriptor.</p>

            {CATALOGO_MODULOS.map(m => (
              <label key={m.key} style={st.modRow}>
                <input type="checkbox" checked={modsSel.includes(m.key)} onChange={() => toggleMod(m.key)} />
                {/* ✅ INV-KARDEX-003 */}
                <span style={(m.key === 'qr' || CLAVES_PREMIUM.includes(m.key)) ? { color: '#8b5cf6', fontWeight: 700 } : {}}>{m.label}</span>
              </label>
            ))}

            {/* Claves existentes fuera del catálogo: se muestran y se conservan */}
            {modsSel.filter(m => !CATALOGO_MODULOS.some(c => c.key === m)).map(m => (
              <label key={m} style={st.modRow}>
                <input type="checkbox" checked onChange={() => toggleMod(m)} />
                <span style={{ color: '#6b6b85' }}>{m} <em style={{ fontSize: 11 }}>(clave personalizada)</em></span>
              </label>
            ))}

            {modsSel.length === 0 && (
              <div style={st.aviso}>Lista vacía = el suscriptor ve <b>todos los módulos operativos</b> (convención del sistema). Los módulos ⭐ premium son la excepción: <b>nunca</b> se activan solos, hay que marcarlos aquí uno por uno.</div>
            )}

            <div style={{ ...st.btns, justifyContent: 'flex-end' }}>
              <button style={st.btnGhost} disabled={guardando} onClick={() => setEditMods(null)}>Cancelar</button>
              <button style={st.btn} disabled={guardando} onClick={guardarMods}>{guardando ? 'Guardando…' : 'Guardar módulos'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ✅ CAPACIDAD-TENANT-002: MODAL ÓRDENES ATASCADAS ─── */}
      {editAtascos && atascos && (
        <div style={st.overlay} onClick={() => !revisando && setEditAtascos(null)}>
          <div style={st.modal} onClick={(ev) => ev.stopPropagation()}>
            <h2 style={{ ...st.h1, fontSize: 17 }}>
              Órdenes atascadas — {editAtascos.empresa || editAtascos.nombre || editAtascos.email}
            </h2>

            {atascos.modo === 'sin_cambios' ? (
              <div style={{ ...st.aviso, marginTop: 12 }}>{atascos.mensaje}</div>
            ) : atascos.total === 0 ? (
              <div style={{ ...st.aviso, marginTop: 12 }}>
                No hay órdenes atascadas. Este suscriptor está limpio.
              </div>
            ) : (
              <>
                <p style={{ ...st.sub, marginTop: 6 }}>
                  {atascos.modo === 'aplicado'
                    ? `Se corrigieron ${atascos.corregidas} de ${atascos.total} orden(es).`
                    : `${atascos.total} orden(es) quedaron detenidas en taller sin que el suscriptor tenga el módulo. Vista previa — todavía no se ha modificado nada.`}
                </p>

                <div style={{ maxHeight: 300, overflowY: 'auto', margin: '12px 0' }}>
                  {atascos.hallazgos.map(h => (
                    <div key={h.id} style={{ padding: '9px 4px', borderBottom: '1px solid #f1f1f8', fontSize: 13 }}>
                      <div style={{ fontWeight: 700, color: '#14142b' }}>
                        {h.numeroOrden} — {h.clienteNombre || 'Sin cliente'}
                      </div>
                      <div style={{ color: '#6b6b85', marginTop: 3 }}>
                        ${Number(h.total || 0).toLocaleString('es-CO')} · {h.estadoActual}
                        {' → '}
                        <b style={{ color: '#16a34a' }}>{h.estadoPropuesto}</b>
                        {h.requiereFactura && !h.numeroFactura && (
                          <span style={{ color: '#d97706' }}> · espera N° de factura DIAN</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ ...st.btns, justifyContent: 'flex-end' }}>
              <button style={st.btnGhost} disabled={revisando}
                onClick={() => { setEditAtascos(null); setAtascos(null); }}>Cerrar</button>
              {atascos.modo === 'vista_previa' && atascos.total > 0 && (
                <button style={st.btn} disabled={revisando}
                  onClick={() => revisarAtascos(editAtascos, true)}>
                  {revisando ? 'Corrigiendo…' : `Corregir ${atascos.total} orden(es)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelSuscriptores;
